import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, withModifiers as _withModifiers, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "mini-player__thumbnail" }
const _hoisted_2 = ["src"]
const _hoisted_3 = { class: "mini-player__text" }
const _hoisted_4 = { class: "mini-player__title" }
const _hoisted_5 = {
  key: 0,
  class: "mini-player__subtitle"
}
const _hoisted_6 = { class: "mini-player__controls" }
const _hoisted_7 = {
  key: 1,
  class: "mini-player__control-icon mini-player__control-icon--play",
  fill: "none",
  viewBox: "0 0 24 24"
}
const _hoisted_8 = {
  key: 2,
  class: "mini-player__control-icon mini-player__control-icon--pause",
  fill: "none",
  viewBox: "0 0 24 24"
}

import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

import { Mixpanel } from '@/analytics/mixpanel'
import { assertNotNull } from '@/helpers/typing'
import { docRouteNames } from '@/models/doc'
import type { DocType } from '@/models/interfaces'
import { playerSession } from '@/services/player'

import CircleSpinner from '@/components/ui/CircleSpinner.vue'

const dismissThreshold = 60

// How far the swipe is towards dismissing the player, from 0 to 1. The
// styles fade the player out as it goes.

export default /*@__PURE__*/_defineComponent({
  __name: 'MiniPlayer',
  setup(__props) {

const doc = computed(() => assertNotNull(playerSession.value.doc))
const route = useRoute()

// The controls are null for docs that have no audio, the player then renders
// as a link back to the doc, without the playback controls.
const playbackControl = computed(() => playerSession.value.playbackControl)
const progressControl = computed(() => playerSession.value.progressControl)

/**
 * The current chapter's title.
 */
const chapterTitle = computed(() => {
  const audio = playerSession.value.audio
  if (audio) {
    return audio.chapters.current().title
  }

  const currentChapter = doc.value.content.find(
    (content) => content.url_slug === doc.value.user.position_chapter,
  )
  return currentChapter?.title || doc.value.title
})

const playerTitle = computed(() => {
  return doc.value.doc_type === 'article' ? doc.value.title : chapterTitle.value
})

const playerSubtitle = computed(() => {
  return doc.value.doc_type === 'article' ? doc.value.author : doc.value.title
})

const progressBarValue = computed(() => {
  const value = progressControl.value
    ? progressControl.value.audioPositionPercent
    : doc.value.user.progress
  return `${value}%`
})

const docLink = computed(() => {
  return {
    name: docRouteNames[doc.value.doc_type as DocType].page,
    params: {
      url_slug: doc.value.url_slug,
      // Articles don't have chapters.
      ...(doc.value.doc_type !== 'article' && {
        page_url_slug: doc.value.user.position_chapter,
      }),
    },
  }
})

const showPlaybackControls = computed(() => {
  // Don't show the controls if the doc doesn't have any audio.
  const hasControls = playbackControl.value !== null
  // Don't show the playback controls if the user
  // hasn't started listening to the audio yet.
  const hasAudioProgress = doc.value.user.audio_position > 0

  return hasControls && hasAudioProgress
})

const swipeOffset = ref(0)
const swipeStartX = ref<number | null>(null)

const isSwiping = computed(() => swipeStartX.value !== null)

// The swipe distance, in pixels, that dismisses the player.
const swipeProgress = computed(() =>
  Math.min(swipeOffset.value / dismissThreshold, 1),
)

onMounted(() => {
  Mixpanel.trackMiniPlayerShown(doc.value, playerState())
})

function togglePlayback(): void {
  const playback = assertNotNull(playbackControl.value)
  const wasPaused = playback.paused
  playerSession.value.togglePlayback({
    entryPoint: 'mini_player',
    referrerPageName: route.fullPath,
  })

  // The play and pause events report the state the tap leads to, which the
  // audio element only reaches a moment later.
  if (wasPaused) {
    Mixpanel.trackMiniPlayerPlay(doc.value, 'playing')
  } else {
    Mixpanel.trackMiniPlayerPause(doc.value, 'paused')
  }
}
function dismiss(): void {
  Mixpanel.trackMiniPlayerDismiss(doc.value, playerState())
  playerSession.value.stop('pause')
}

function onClick(): void {
  playerSession.value.clearNextArrival()
  Mixpanel.trackMiniPlayerClick(doc.value, playerState())
}

function onTouchStart(event: TouchEvent): void {
  swipeStartX.value = event.touches[0].clientX
}

function onTouchMove(event: TouchEvent): void {
  if (swipeStartX.value === null) {
    return
  }

  const distance = swipeStartX.value - event.touches[0].clientX
  // Only track leftwards swipes, the player does not move right.
  swipeOffset.value = Math.max(0, distance)
}

function onTouchEnd(): void {
  const dismissed = swipeOffset.value >= dismissThreshold

  swipeStartX.value = null
  swipeOffset.value = 0

  if (dismissed) {
    dismiss()
  }
}

function playerState(): 'playing' | 'paused' | 'resume' {
  if (!showPlaybackControls.value) {
    return 'resume'
  }

  return playerSession.value.isPlaying ? 'playing' : 'paused'
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["mini-player", { 'mini-player--swiping': isSwiping.value }]),
    style: _normalizeStyle({
      '--player-progress': progressBarValue.value,
      '--player-swipe-offset': `${swipeOffset.value}px`,
      '--player-swipe-progress': swipeProgress.value,
    })
  }, [
    _createElementVNode("div", {
      class: "mini-player__wrapper",
      onTouchstart: onTouchStart,
      onTouchmove: onTouchMove,
      onTouchend: onTouchEnd,
      onTouchcancel: onTouchEnd
    }, [
      _createVNode(_unref(RouterLink), {
        to: docLink.value,
        class: "mini-player__info",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (onClick()))
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("img", {
              class: "mini-player__cover",
              src: doc.value.cover_image
            }, null, 8, _hoisted_2)
          ]),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("h3", _hoisted_4, _toDisplayString(playerTitle.value), 1),
            (playerSubtitle.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(playerSubtitle.value), 1))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }, 8, ["to"]),
      _createElementVNode("div", _hoisted_6, [
        (showPlaybackControls.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "mini-player__control mini-player__control--play",
              onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (togglePlayback()), ["stop"]))
            }, [
              (
              !playbackControl.value?.paused && playbackControl.value?.interruptedByNetwork
            )
                ? (_openBlock(), _createBlock(CircleSpinner, {
                    key: 0,
                    "animation-duration": 2000,
                    size: 58,
                    color: '#ffc906'
                  }))
                : (playbackControl.value?.paused)
                  ? (_openBlock(), _createElementBlock("svg", _hoisted_7, [...(_cache[4] || (_cache[4] = [
                      _createElementVNode("path", {
                        d: "M17.2 13.7a2 2 0 0 0 0-3.4L10 5.9a2 2 0 0 0-3 1.7v8.8a2 2 0 0 0 3 1.7z",
                        fill: "currentColor"
                      }, null, -1)
                    ]))]))
                  : (_openBlock(), _createElementBlock("svg", _hoisted_8, [...(_cache[5] || (_cache[5] = [
                      _createElementVNode("rect", {
                        x: "8",
                        y: "5",
                        width: "2",
                        height: "14",
                        rx: "1",
                        fill: "currentColor"
                      }, null, -1),
                      _createElementVNode("rect", {
                        x: "14",
                        y: "5",
                        width: "2",
                        height: "14",
                        rx: "1",
                        fill: "currentColor"
                      }, null, -1)
                    ]))]))
            ]))
          : (_openBlock(), _createBlock(_unref(RouterLink), {
              key: 1,
              to: docLink.value,
              class: "mini-player__control mini-player__control--chevron",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (onClick()))
            }, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createElementVNode("span", { class: "iconfont iarrow" }, null, -1)
              ]))]),
              _: 1
            }, 8, ["to"]))
      ]),
      _createElementVNode("button", {
        class: "mini-player__close",
        onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (dismiss()), ["stop"]))
      }, [...(_cache[7] || (_cache[7] = [
        _createElementVNode("span", { class: "iconfont iclose" }, null, -1)
      ]))])
    ], 32)
  ], 6))
}
}

})